import { HttpStatusCode } from 'axios';
import { toast } from 'react-toastify';
import i18n from '../../i18n/i18nInit';
import { API_CONSTANTS } from '../../services/apiConstants';
import {
    AttachmentTypes,
    AutoCloseTimeoutConstants,
} from '../../utils/constants';
import { IGenericApiResponse, IHttpCodeResponse } from '../../utils/interfaces';
import { apiCall } from '../axios';
import { IAttachmentResponse } from '../interfaces';
import { IAllDriverLoadsInvoice } from './interfaces/IAllDriverLoadsInvoice';
import { IDriverLoad } from './interfaces/IDriverLoad';
import { IAssociateAttachment } from '../../pages/Driver/DriverDetails/interfaces/IAssociateAttachment';
import {
    IDriverLoadCommodityResponseModel,
    IDriverLoadWithContacts,
} from './interfaces';

export const attachBillOfLading = async (
    shipmentId: number,
    driverLoadId: number | string,
    uploadedFile: File | undefined,
    type: string,
): Promise<void> => {
    const formData = new FormData();
    if (uploadedFile) {
        formData.append('File', uploadedFile);
        formData.append('Type', type);
    }

    const response: IHttpCodeResponse = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.loads}/${driverLoadId}${API_CONSTANTS.attachments}`,
        formData,
        true,
        false,
        true,
    );

    if (
        response &&
        response.status >= HttpStatusCode.Ok &&
        response.status < HttpStatusCode.MultipleChoices
    ) {
        const message = i18n.t(
            'LB_AttachBillOfLading:billAttachedSuccessfully',
        );
        toast.success(message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: AutoCloseTimeoutConstants.toasterAutoClose,
        });
    }
};

export const deleteBillOfLading = async (
    shipmentId: number | string,
    driverLoadId: number | null,
    attachmentId: number,
): Promise<void> => {
    const response = await apiCall(
        'delete',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.loads}/${driverLoadId}${API_CONSTANTS.attachments}/${attachmentId}`,
        {},
        true,
    );

    return response;
};

export const createDriverLoad = async (
    shipmentId: number | string,
    loads: IDriverLoad,
): Promise<number[]> => {
    const response = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.loads}`,
        { ...loads },
        true,
    );

    return response.data as number[];
};

export const attachAllDriverLoadsInvoice = async (
    invoiceDetails: IAllDriverLoadsInvoice,
): Promise<void> => {
    const formData = new FormData();
    const attachmentIndex = 0;
    const prefix = `Attachment[${attachmentIndex}]`;

    if (invoiceDetails.uploadedFile || invoiceDetails.uploadedFile === null) {
        const file: File | string =
            invoiceDetails.uploadedFile !== null
                ? (invoiceDetails.uploadedFile as File)
                : 'null';

        formData.append(`${prefix}.File`, file);
        formData.append(
            `${prefix}.Id`,
            invoiceDetails.shipmentId?.toString() || '',
        );
        if (invoiceDetails.customerId !== undefined) {
            formData.append(
                `${prefix}.CustomerId`,
                invoiceDetails.customerId.toString(),
            );
        }
        formData.append(`${prefix}.Type`, AttachmentTypes.invoice);
    }

    formData.append(
        `${prefix}.IsBolRequired`,
        invoiceDetails.isBOLRequired.toString(),
    );
    formData.append(
        `${prefix}.IsScaleTicketRequired`,
        invoiceDetails.isScaleTicketRequired.toString(),
    );
    formData.append(
        `${prefix}.IsOtherRequired`,
        invoiceDetails.isOtherRequired.toString(),
    );
    formData.append(
        `${prefix}.IsQuickbooksRequired`,
        invoiceDetails.isQuickbooksRequired.toString(),
    );
    formData.append(`${prefix}.IsResend`, invoiceDetails.isResend.toString());
    formData.append(
        `${prefix}.Recipients`,
        JSON.stringify(invoiceDetails.recipients),
    );

    const response: IHttpCodeResponse = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}${API_CONSTANTS.invoice}`,
        formData,
        true,
        false,
        true,
    );

    if (
        response &&
        response.status >= HttpStatusCode.Ok &&
        response.status < HttpStatusCode.MultipleChoices
    ) {
        const message = i18n.t('LB_InvoiceDetails:invoiceSent');
        toast.success(message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: AutoCloseTimeoutConstants.toasterAutoClose,
        });
    }
};

export const uploadDriverLoadAttachment = async (
    shipmentId: number,
    driverLoadId: number,
    attachment: File | null | string,
    fileType?: string,
): Promise<IAttachmentResponse> => {
    const formData = new FormData();

    attachment && formData.append('File', attachment);
    fileType && formData.append('Type', fileType);

    const response: IGenericApiResponse<IAttachmentResponse> = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.loads}/${driverLoadId}${API_CONSTANTS.attachments}`,
        formData,
        true,
        false,
        true,
    );

    return response.data;
};

export const updateDriverLoad = async (
    shipmentId: number | string,
    loads: IDriverLoad,
    driverLoadId: number | null,
): Promise<void> => {
    const filteredDriverLoadCharges = loads.driverLoadCharges.filter(
        charge => charge.driverLoadChargeType !== '',
    );

    const updatedLoads: IDriverLoad = {
        ...loads,
        driverLoadCharges: filteredDriverLoadCharges,
    };

    const response = await apiCall(
        'put',
        `${API_CONSTANTS.shipments}/${shipmentId}/loads/${driverLoadId}`,
        { ...updatedLoads },
        true,
    );

    return response;
};

export const deleteDriverLoad = async (
    shipmentId: number | string,
    driverLoadId: number | null,
): Promise<void> => {
    const response = await apiCall(
        'delete',
        `${API_CONSTANTS.shipments}/${shipmentId}/loads/${driverLoadId}`,
        {},
        true,
    );

    return response;
};

export const fetchDriverLoadAttachment = async (
    shipmentId: number,
    driverLoadId: number | null,
    attachment: IAttachmentResponse | null,
): Promise<IGenericApiResponse<Blob>> => {
    const response: IGenericApiResponse<Blob> = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}/${shipmentId}/loads/${driverLoadId}/attachments/${attachment?.attachmentId}/${attachment?.accessToken}`,
        {},
        true,
        true,
    );

    return response;
};

const fetchAttachmentByDriverLoad = async (
    shipmentId: number,
): Promise<IAssociateAttachment[]> => {
    const response: IGenericApiResponse<IAssociateAttachment[]> = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}/${shipmentId}/loads/attachments`,
        {},
        true,
    );

    return response.data;
};

const fetchDriverLoadContacts = async (
    shipmentId: number,
): Promise<IDriverLoadWithContacts[]> => {
    const response: IGenericApiResponse<IDriverLoadWithContacts[]> =
        await apiCall(
            'get',
            `${API_CONSTANTS.shipments}/${shipmentId}/loads/contacts`,
            {},
            true,
        );

    return response.data;
};

const fetchDriverLoadCommodities = async (
    shipmentId: number,
): Promise<IDriverLoadCommodityResponseModel[]> => {
    const response: IGenericApiResponse<IDriverLoadCommodityResponseModel[]> =
        await apiCall(
            'get',
            `${API_CONSTANTS.shipments}/${shipmentId}/loads/commodities`,
            {},
            true,
        );

    return response.data;
};

const driverLoadApi = {
    createDriverLoad,
    uploadDriverLoadAttachment,
    updateDriverLoad,
    deleteDriverLoad,
    deleteBillOfLading,
    fetchDriverLoadAttachment,
    fetchAttachmentByDriverLoad,
    fetchDriverLoadContacts,
    fetchDriverLoadCommodities,
};

export default driverLoadApi;
